import { useEffect, useRef, useState } from "react";

import * as THREE from "three/webgpu";
import { pass } from "three/tsl";
import { bloom } from "three/addons/tsl/display/BloomNode.js";
import { GalaxySimulation } from "./galaxy/GalaxySimulation";

const BLOOM = {
  strength: 0.1,
  radius: 0.37,
  threshold: 0.32,
};

const CONFIG = {
  starCount: 200000,
  cloudCount: 600,
  galaxyRadius: 19.67,
  galaxyThickness: 8.6,
  spiralTightness: 1.63,
  armCount: 2,
  armWidth: 3.04,
  randomness: 3.91,
  rotationSpeed: 0.26,
  mouseForce: 4.57,
  mouseRadius: 14.54,
  particleSize: 0.06,
  cloudSize: 1.6,
  cloudOpacity: 0.012,
  starBrightness: 0.37,
  denseStarColor: "#25a9fc",
  sparseStarColor: "#ff96d3",
  cloudTintColor: "#fff7e4",
};

export default function GalaxyHero() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "unsupported">(
    "loading",
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let renderer: any = null;
    let sim: GalaxySimulation | null = null;
    let raf = 0;
    const cleanupFns: (() => void)[] = [];

    (async () => {
      // Detect WebGPU support
      const hasWebGPU =
        typeof navigator !== "undefined" && (navigator as any).gpu;

      if (!hasWebGPU) {
        setStatus("unsupported");
        return;
      }

      try {
        renderer = new THREE.WebGPURenderer({
          antialias: true,
          powerPreference: "high-performance",
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(container.clientWidth, container.clientHeight);
        renderer.setClearColor(0x000000, 1);
        await renderer.init();

        if (disposed) {
          renderer.dispose();
          return;
        }

        container.appendChild(renderer.domElement);
        renderer.domElement.style.display = "block";
        renderer.domElement.style.cursor = "grab";
        renderer.domElement.style.touchAction = "none";

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(
          55,
          container.clientWidth / container.clientHeight,
          0.1,
          500,
        );
        camera.position.set(0, 4.5, 16);
        camera.lookAt(0, -2, 0);

        sim = new GalaxySimulation(scene, CONFIG);
        sim.createGalaxySystem();
        sim.createClouds();

        // ----- Post-processing (bloom) -----
        const postProcessing = new THREE.PostProcessing(renderer);
        const scenePass = pass(scene, camera);
        const bloomPass = bloom(
          scenePass,
          BLOOM.strength,
          BLOOM.radius,
          BLOOM.threshold,
        );
        postProcessing.outputNode = scenePass.add(bloomPass);

        // ----- Mouse interaction -----
        const raycaster = new THREE.Raycaster();
        const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        const intersectionPoint = new THREE.Vector3();
        const mouse3D = new THREE.Vector3();
        let mousePressed = false;

        function getNDC(e: PointerEvent) {
          const rect = renderer.domElement.getBoundingClientRect();
          return new THREE.Vector2(
            ((e.clientX - rect.left) / rect.width) * 2 - 1,
            -((e.clientY - rect.top) / rect.height) * 2 + 1,
          );
        }
        function updateMouse(e: PointerEvent) {
          raycaster.setFromCamera(getNDC(e), camera);
          if (raycaster.ray.intersectPlane(dragPlane, intersectionPoint)) {
            mouse3D.copy(intersectionPoint);
          }
        }
        function onPointerDown(e: PointerEvent) {
          mousePressed = true;
          renderer.domElement.style.cursor = "grabbing";
          updateMouse(e);
          try {
            renderer.domElement.setPointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
        }
        function onPointerMove(e: PointerEvent) {
          updateMouse(e);
        }
        function onPointerUp(e: PointerEvent) {
          mousePressed = false;
          renderer.domElement.style.cursor = "grab";
          try {
            renderer.domElement.releasePointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
        }
        function onPointerLeave() {
          mousePressed = false;
          renderer.domElement.style.cursor = "grab";
        }

        renderer.domElement.addEventListener("pointerdown", onPointerDown);
        renderer.domElement.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        renderer.domElement.addEventListener("pointerleave", onPointerLeave);
        cleanupFns.push(() => {
          renderer.domElement.removeEventListener("pointerdown", onPointerDown);
          renderer.domElement.removeEventListener("pointermove", onPointerMove);
          window.removeEventListener("pointerup", onPointerUp);
          renderer.domElement.removeEventListener(
            "pointerleave",
            onPointerLeave,
          );
        });

        // ----- Resize -----
        function onResize() {
          if (!container || !renderer) return;
          const w = container.clientWidth;
          const h = container.clientHeight;
          renderer.setSize(w, h);
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
        }
        const ro = new ResizeObserver(onResize);
        ro.observe(container);
        cleanupFns.push(() => ro.disconnect());

        setStatus("ready");

        // ----- Animation loop -----
        let last = performance.now();
        const tmp = new THREE.Vector3();
        const clock = new THREE.Clock();

        async function tick() {
          if (disposed) return;
          raf = requestAnimationFrame(tick);
          const now = performance.now();
          const dt = Math.min(0.05, (now - last) / 1000);
          last = now;

          if (sim) {
            await sim.update(renderer, dt, mouse3D, mousePressed);
          }

          // Subtle camera drift
          const et = clock.getElapsedTime();
          tmp.set(
            Math.sin(et * 0.05) * 0.5,
            4.5 + Math.sin(et * 0.08) * 0.3,
            16 + Math.cos(et * 0.04) * 0.5,
          );
          camera.position.lerp(tmp, 0.02);
          camera.lookAt(0, -2, 0);

          await postProcessing.renderAsync();
        }
        tick();
      } catch (err) {
        console.error("Galaxy simulation failed to initialize:", err);
        setStatus("unsupported");
      }
    })();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      cleanupFns.forEach((fn) => fn());
      if (renderer) {
        try {
          renderer.dispose();
        } catch {
          /* ignore */
        }
        if (
          renderer.domElement &&
          renderer.domElement.parentNode === container
        ) {
          container.removeChild(renderer.domElement);
        }
      }
    };
  }, []);

  return (
    <section className="relative w-full min-h-screen overflow-hidden bg-black select-none">
      {/* Galaxy canvas — full background */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* Vignette */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_45%,_rgba(0,0,0,0.55)_100%)]" />

      {/* Hero title — transparent overlay above the galaxy */}
      <div className="pointer-events-none relative z-10 w-full flex flex-col items-center text-center px-6 pt-14 pb-6">
        <h1
          className="max-w-5xl text-5xl md:text-7xl lg:text-[5.5rem] font-light leading-[1.02] tracking-tight text-white"
          style={{
            fontFamily: "'Instrument Serif', 'Cormorant Garamond', serif",
          }}
        >
          A galaxy of
          <span className="block italic text-white">
            two hundred thousand stars
          </span>
        </h1>
      </div>

      {/* Drag hint */}
      <div
        className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.4em] text-white z-10"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
      >
        ↓ drag · swirl · scroll
      </div>

      {/* Status overlay */}
      {status === "loading" && (
        <div
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center text-white/70 text-sm"
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        >
          Initializing GPU…
        </div>
      )}
      {status === "unsupported" && (
        <div className="absolute inset-0 z-20 flex items-center justify-center px-6">
          <div
            className="max-w-md rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md px-6 py-5 text-center text-white/80"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            <div className="text-2xl mb-2">🌌</div>
            <div className="text-sky-300 font-semibold mb-1">
              WebGPU not available
            </div>
            <p className="text-xs text-white/60 leading-5">
              This simulation runs on the GPU using WebGPU compute shaders. Try
              the latest Chrome, Edge, or Safari Technology Preview to see it
              live.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}